import { noop } from "lodash";
import {
  ComponentProps,
  FunctionComponent,
  useCallback,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { Button } from "../../../../components/button";
import { Checkbox } from "../../../../components/forms/checkbox";
import { Icon } from "../../../../components/icons";
import { Widget } from "../../../../components/widget";
import { UserActions } from "../../../../store/users/actions";
import { UsersDTO } from "../../../../store/users/models/dto";
import { TwoFAProviders } from "../../../../store/users/models/internal";
import { UsersService } from "../../../../store/users/service";
import { StyledSpinner } from "../../all/base";

export const StyledWidget = styled(Widget)`
  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;

export const StyledCheckbox = styled(Checkbox)`
  padding: 0;
`;

export const Label = styled.span`
  font-weight: 500;
`;

export const ResetTFA: FunctionComponent<{
  userId: UsersDTO.Responses.GetSingle.T["userReferenceId"];
  provider: typeof TwoFAProviders.T;
}> = ({ provider, userId }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  const onClick = useCallback(() => {
    setLoading(true);
    UsersService.delete2FA(provider, userId)
      .then(() =>
        dispatch(UserActions.getSingleUser({ userId, refetch: true }))
      )
      .catch(noop)
      .finally(() => setLoading(false));
  }, [provider]);

  return loading ? (
    <StyledSpinner />
  ) : (
    <Button onClick={onClick} colour="warning">
      <Icon type="reportAScam" />
    </Button>
  );
};

export const ResendEmailVerification: FunctionComponent<{
  userId: UsersDTO.Responses.GetSingle.T["userReferenceId"];
  email: string;
  className?: string;
}> = ({ email, userId, className }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const onClick = useCallback(() => {
    setLoading(true);
    UsersService.resendVerificationEmail(email)
      .catch(noop)
      .finally(() => setLoading(false));
  }, [email]);

  return loading ? (
    <StyledSpinner className={className} />
  ) : (
    <Button onClick={onClick} className={className}>
      <Icon type="email" />
    </Button>
  );
};

export const Update2FA: FunctionComponent<{
  userId: UsersDTO.Responses.GetSingle.T["userReferenceId"];
  provider?: typeof TwoFAProviders.T;
  className?: string;
}> = ({ userId, provider, className }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  const onClick = useCallback(() => {
    setLoading(true);
    UsersService.update2FA(userId, provider)
      .then(() =>
        dispatch(UserActions.getSingleUser({ userId, refetch: true }))
      )
      .catch(noop)
      .finally(() => setLoading(false));
  }, [userId, provider]);

  return loading ? (
    <StyledSpinner className={className} />
  ) : (
    <Button
      onClick={onClick}
      className={className}
      colour={provider ? "success" : "danger"}
    >
      {provider ? "Set Default" : "Disable 2FA "}
    </Button>
  );
};

const AbstractLockingCTA: FunctionComponent<{
  userId: UsersDTO.Responses.GetSingle.T["userReferenceId"];
  type: "lock" | "unlock";
  reloadUserOnly: boolean;
  className?: string;
}> = ({ userId, type, reloadUserOnly, className }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  const onClick = useCallback(() => {
    setLoading(true);
    Promise.resolve()
      .then(() =>
        type === "lock"
          ? UsersService.lockUser(userId)
          : UsersService.unlockUser(userId)
      )
      .then(() =>
        dispatch(
          reloadUserOnly
            ? UserActions.getSingleUser({ userId, refetch: true })
            : UserActions.getUsers()
        )
      )
      .catch(noop)
      .finally(() => setLoading(false));
  }, [userId]);

  return loading ? (
    <StyledSpinner className={className} />
  ) : (
    <Button
      onClick={onClick}
      colour={type === "lock" ? "danger" : "success"}
      className={className}
    >
      <Icon type={type === "lock" ? "padlockClosed" : "padlockOpen"} />
    </Button>
  );
};

export const LockingDetailCTA: FunctionComponent<
  Omit<ComponentProps<typeof AbstractLockingCTA>, "reloadUserOnly">
> = (props) => <AbstractLockingCTA {...props} reloadUserOnly />;

export const LockingTableCTA: FunctionComponent<
  Omit<ComponentProps<typeof AbstractLockingCTA>, "reloadUserOnly">
> = (props) => <AbstractLockingCTA {...props} reloadUserOnly={false} />;
